import FilterAltIcon from "@mui/icons-material/FilterAltOutlined";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import AssignmentLateIcon from "@mui/icons-material/AssignmentLate";
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";
import {
  Alert,
  Box,
  Button,
  Container,
  Divider,
  IconButton,
  MenuItem,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import SchedulerMonthCalendar from "./MonthCalendar";
import { useCallback, useEffect, useState } from "react";
import SchedulerAvailableTimes from "./AvailableTimes";
import SchedulerPatientBrief from "./PatientBrief";
import SchedulerFilterDialog, {
  SchedulerFilterDialogInput,
} from "./FilterDialogModal";
import SchedulerBestAvailableOptions from "./BestAvailableOptions";
import { ApolloError, gql } from "@apollo/client";
import { useMemo } from "react";
import { DashboardTile } from "./DashboardTiles";
import SchedulerSelectedOptionDetail from "./SelectionOptionDetail";
import { addDays, formatISO } from "date-fns";
import { DayCalendarSkeleton } from "@mui/x-date-pickers";
import {
    PatientDetailQuery,
  SchedulerOptionsQuery,
  SchedulerScreenVisitQuery,
  useSchedulerOptionsQuery,
  useSchedulerOrganizationalUnitsQuery,
  useSchedulerProvidersListQuery,
  useScheduleVisitMutation,
} from "~/graphql/__generated__/graphql";
import { useOrgConfig } from "~/providers/OrgConfigProvider";
import SchedulerFilterChips from "./FilterChips";
import _, { sortBy, startCase, uniq } from "lodash";
import { dbDate, datef } from "@caredna-marlin/utils/dates";
import { bestOptions, highestRank, rankColor } from "~/utils/scheduleOptions";
import {
  useZonedDateFormat,
} from "~/providers/ZonedDateFormatProvider";
import ServiceInstructionsInput from "./ServiceInstructionsInput";
import VisitNoteInput from "./VisitNoteInput";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SchedulingError } from "@caredna-marlin/utils/errorCodes";
import { CareTeamWarning } from "../common/CareTeamWarning";
import { addMarketListVisit } from "~/services/demo/marketListStore";
import { useSnackbar } from "~/hooks/useSnackbar";
import SchedulerParametersDialog, { SchedulerParametersDialogInput, setServiceLine } from "./ParameterDialogModal";
import { setVisitType } from "../VisitTypeDuration";
import LabeledSelect from "../common/forms/LabeledSelect";
import { useCurrentOrg } from "~/providers/GraphqlProvider";
import { useOrganizationalUnitSegmentationEnabled } from "~/hooks/permissions";
import { getSchedulingOrganizationalUnitId } from "~/utils/visitsHelper";

export type SchedulerOption = SchedulerOptionsQuery["scheduleOption"]["options"][number];
export type DecoratedSchedulerOption = SchedulerOption & {
  dbDate: string;
  index: number;
};
export type SchedulerReschedulingVisit =
  SchedulerScreenVisitQuery["visitSingle"];

type Props = {
  visit?: SchedulerReschedulingVisit;
  selectedPatient?: PatientDetailQuery["patientSingle"] | null | undefined;
  loading: boolean;
};

export default function SchedulerScreen(props: Props) {
  const orgConfig = useOrgConfig();
  const [searchParams] = useSearchParams();


  const initialParams = useMemo(() => {
    const requestedServiceLineId = props.visit?.episode?.serviceLineId.toString() || searchParams.get('serviceLine') || Object.values(orgConfig.serviceLines)[0]?.id.toString() || "";

    const requestedVisitTypeId = props.visit?.visitTypeId.toString() || searchParams.get('visitType') || "";
    const targetTimeFrom = props.visit?.createdAt ? new Date(props.visit.createdAt) : new Date();

    const initialValues = {
      ...DEFAULT_TASK_DETAIL,
      durationLocked: false,
      targetTimeFrom,
      targetTime: targetTimeFrom,
    };

    // Sets the default services, duration, verifies that the visit type is part of the service line, etc.
    return setServiceLine(requestedServiceLineId, { ...initialValues, ...setVisitType(requestedVisitTypeId, initialValues, orgConfig)}, orgConfig);
  }, [props.visit, searchParams, orgConfig]);

  // key is used here to force re-rendering when props change, when the visit or orgConfig finish loading.
  return <SchedulerScreenComponent
    key={`${props.visit?.id}-${initialParams.visitTypeId}`}
    {...props}
    loading={props.loading || orgConfig.loading}
    initialParams={initialParams}
  />;
}

type TaskDetail = {
  serviceLineId: string;
  visitTypeId: string;
  serviceIds: string[];
  serviceNames: string[];
  duration: string;
  episodeId: string;
  constraints: {
    earliestStartTime?: Date;
    latestStartTime?: Date;
  },
};

const MAX_RACE_CONDITION_RETRIES = 1;
const defaultFilters = {
  daysOfWeek: [],
  timeOfDay: "",
  providerGender: "",
  providerLanguage: "",
  provider: [],
  useOverflowShifts: false,
};

function SchedulerScreenComponent({ visit, selectedPatient, loading, initialParams }: Props & { initialParams: SchedulerParametersDialogInput }) {
  const { visitTypes, providerTypes } = useOrgConfig();
  const organizationUserId = useCurrentOrg()?.organizationUserId;
  const organizationalUnitSegmentationEnabled = useOrganizationalUnitSegmentationEnabled();

  const navigate = useNavigate();
  const { setErrorMessage: showSnackbar } = useSnackbar();

  const patient = selectedPatient || visit?.patient;
  const patientId = patient?.id;

  // Visit Marketplace demo: always show "no options" for this patient
  const MARKETPLACE_PATIENT_KEY = 'demo-marketplace-patient-id';
  const marketplacePatientId = (() => {
    try { const v = localStorage.getItem(MARKETPLACE_PATIENT_KEY); return v ? Number(v) : null; } catch { return null; }
  })();
  const isMarketplacePatient = !!patientId && patientId === marketplacePatientId;

  const timezone = patient?.addressTimezone;
  const { timezoneAbbreviation } = useZonedDateFormat();

  const [parametersModalOpen, setParametersModalOpen] = useState(false);
  const [parameterValues, setParameterValues] = useState(initialParams);
  const [parametersApplied, setParametersApplied] = useState(false);

  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [filterValues, setFilterValues] = useState<SchedulerFilterDialogInput>({ ...defaultFilters });

  const visitType = useMemo<typeof visitTypes[string] | undefined>(() => visitTypes[parameterValues.visitTypeId], [parameterValues.visitTypeId, visitTypes]);
  const visitTypeAllowsOverflowShifts = useMemo(() => visitType?.schedulingConfig.allowOverflowShifts || false, [visitType]);

  const [serviceInstructions, setServiceInstructions] = useState(visit?.serviceInstructions || patient?.serviceInstructions || "");
  const [visitNote, setVisitNote] = useState("");

  const hourRangeFromTimeOfDay = (timeOfDay: string) => {
    switch (timeOfDay) {
      case "morning":
        return { startHour: 0, endHour: 12 };
      case "afternoon":
        return { startHour: 12, endHour: 23 };
      default:
        console.error(`Unknown timeOfDay ${timeOfDay}`);
        return { startHour: 0, endHour: 23 };
    }
  };

  const serviceLineId = parseInt(parameterValues.serviceLineId);
  const activeEpisode = patient?.episodes.find((episode) =>
    episode.active && episode.serviceLineId === serviceLineId
  );
  const creatingEpisode = !visit && !activeEpisode;
  const {
    data: organizationalUnitsData,
    loading: organizationalUnitsLoading,
    error: organizationalUnitsError,
  } = useSchedulerOrganizationalUnitsQuery({
    variables: { organizationUserId: organizationUserId ?? 0 },
    skip: !!visit,
    fetchPolicy: "network-only",
  });
  const organizationUnits = organizationalUnitsData?.organizationalUnit
    .filter((unit) => unit.active)
    .map(({ id, name, active }) => ({ id, name, active })) ?? [];
  const assignedOrganizationalUnits = organizationalUnitsData?.organizationalUnitOrgUser
    .flatMap((membership) => membership.organizationalUnit?.active
      ? [membership.organizationalUnit]
      : [])
    .map(({ id, name, active }) => ({ id, name, active })) ?? [];
  const activeOrganizationalUnits = sortBy(
    organizationalUnitSegmentationEnabled === false || organizationUnits.length === 1
      ? organizationUnits
      : assignedOrganizationalUnits,
    "name",
  );
  const episodeOrganizationalUnitId = visit
    ? visit.episode?.organizationalUnitId ?? null
    : activeEpisode
      ? activeEpisode.organizationalUnitId
      : getSchedulingOrganizationalUnitId(
        undefined,
        activeOrganizationalUnits,
        parameterValues.organizationalUnitId,
        organizationUnits.length === 1,
      ) ?? null;
  const filterOrganizationalUnitId = organizationalUnitSegmentationEnabled
    ? episodeOrganizationalUnitId
    : null;
  const organizationalUnitSegmentationLoading = organizationalUnitSegmentationEnabled == null;
  const noActiveOrganizationalUnits = !organizationalUnitsLoading && !organizationalUnitsError
    && organizationalUnitsData?.organizationalUnit.every((unit) => !unit.active) === true;
  const requiresOrganizationalUnitSelection = creatingEpisode
    && episodeOrganizationalUnitId == null
    && !noActiveOrganizationalUnits;
  const requiresOrganizationalUnitSelectionForProviders = organizationalUnitSegmentationEnabled === true
    && requiresOrganizationalUnitSelection;

  useEffect(() => {
    if (selectedPatient?.id && (!parameterValues.visitTypeId || (requiresOrganizationalUnitSelectionForProviders && organizationUnits.length > 1))) {
      setParametersModalOpen(true);
    }
  }, [selectedPatient, parameterValues.visitTypeId, requiresOrganizationalUnitSelectionForProviders, organizationUnits.length]);

  const useOverflowShifts = visitTypeAllowsOverflowShifts && filterValues.useOverflowShifts;

  const queryFilterValues = useMemo(() => ({
    patientId: patientId!,
    startTime: formatISO(new Date()),
    targetTime: formatISO(parameterValues.targetTime),
    endTime: formatISO(addDays(new Date(), visitType?.schedulingConfig.daysBeforeLastOption || 0)),
    serviceLineId,
    organizationalUnitId: filterOrganizationalUnitId,
    visitTypeId: visitType?.id!,
    duration: parseInt(parameterValues.duration),
    providerTraits: [
      ...(filterValues.providerGender
        ? [{ trait: "gender", value: filterValues.providerGender }]
        : []),
      ...(filterValues.providerLanguage
        ? [{ trait: "language", value: filterValues.providerLanguage }]
        : []),
    ],
    serviceIds: parameterValues.serviceIds.map((serviceId) => {
      return parseInt(serviceId);
    }),
    serviceNames: parameterValues.serviceNames,
    daysOfWeek:
      filterValues.daysOfWeek.length > 0
        ? filterValues.daysOfWeek.map((day) => Number.parseInt(day))
        : null,
    hoursOfDay: filterValues.timeOfDay
      ? hourRangeFromTimeOfDay(filterValues.timeOfDay)
      : null,
    reschedulingVisitId: visit?.id || null,
    selectedProviderIds: filterValues.provider?.map((p) => parseInt(p)),
    useOverflowShifts,
  }), [filterOrganizationalUnitId, filterValues, parameterValues, patientId]);

  const queryProvidersListFilters = useMemo(
    () => ({
      patientId: patientId!,
      startTime: formatISO(new Date()),
      endTime: formatISO(addDays(new Date(), visitType?.schedulingConfig.daysBeforeLastOption || 14)), //2 weeks
      serviceLineId,
      organizationalUnitId: filterOrganizationalUnitId,
      visitTypeId: visitType?.id!,
    }),
    [filterOrganizationalUnitId, patientId, serviceLineId, visitType]
  );

  const { data: scheduleProvidersData } = useSchedulerProvidersListQuery({
    skip: !(patientId && visitType && serviceLineId)
      || organizationalUnitSegmentationLoading
      || requiresOrganizationalUnitSelectionForProviders,
    variables: queryProvidersListFilters,
  });

  const shouldFetchScheduleData =
    (visit || patientId) &&
    queryFilterValues?.duration > 0 &&
    queryFilterValues?.visitTypeId !== undefined &&
    queryFilterValues?.serviceLineId !== undefined &&
    !organizationalUnitSegmentationLoading &&
    !requiresOrganizationalUnitSelectionForProviders
    ;

  const { data: scheduleData, loading: scheduleLoading, refetch: refetchSchedule } = useSchedulerOptionsQuery({
    onError(error) {
      console.error(error.message);
    },
    variables: queryFilterValues,
    skip: !shouldFetchScheduleData,
  });

  const scheduleOptions = useMemo<DecoratedSchedulerOption[]>(() => {
    if (isMarketplacePatient) return []; // always force empty for demo
    if (!scheduleData?.scheduleOption || (organizationalUnitSegmentationEnabled && (!shouldFetchScheduleData || scheduleLoading))) {
      return [];
    }
    const decorated = scheduleData.scheduleOption.options.map((option, index) => ({
      ...option,
      dbDate: dbDate(option.startTime),
      index: index,
    }));
    return sortBy(decorated, "startTime");
  }, [scheduleData, isMarketplacePatient, organizationalUnitSegmentationEnabled, shouldFetchScheduleData, scheduleLoading]);

  // Assemble date badges for calendar
  const dateBadges = useMemo<{ [date: string]: string }>(() => {
    if (!scheduleOptions) {
      return {};
    }
    const dates = uniq(scheduleOptions.map((option) => option.dbDate));
    return dates.reduce((acc, date) => {
      const options = scheduleOptions.filter(
        (option) => option.dbDate === date
      );
      return {
        ...acc,
        [date]: rankColor(highestRank(options)),
      };
    }, {});
  }, [scheduleOptions]);

  const best3Options = useMemo(() => {
    return bestOptions(scheduleOptions, 3);
  }, [scheduleOptions]);

  const [selectedOption, setSelectedOption] =
    useState<DecoratedSchedulerOption | null>(null);

  useEffect(() => {
    setSelectedOption(null);
    setFilterValues((values) => values.provider.length > 0
      ? { ...values, provider: [] }
      : values);
  }, [filterOrganizationalUnitId]);

  const [submitting, setSubmitting] = useState(false);

  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const providersById = useMemo<Record<number, { id: number, firstName: string, lastName: string }>>(() => {
    return scheduleData?.scheduleOption.providers.reduce((dict, provider) => {
      dict[provider.id] = provider;
      return dict;
    }, {} as any) || {};
  }, [scheduleData]);

  // If the options change, the selected option must be reset
  useEffect(() => {
    if (selectedOption) {
      setSelectedOption(null);
    }
  }, [scheduleOptions]);

  // Pre-select the best option first
  useEffect(() => {
    if (!selectedOption && best3Options.length > 0) {
      setSelectedOption(best3Options[0]);
      setSelectedDate(new Date(best3Options[0].arrivalWindowStart));
    }
  }, [best3Options]);

  useEffect(() => {
    if (selectedOption && dbDate(selectedDate) != selectedOption.dbDate) {
      setSelectedDate(new Date(selectedOption?.arrivalWindowStart));
    }
  }, [selectedOption]);

  useEffect(() => {
    if (selectedDate && selectedDateOptions.length > 0) {
      if (
        !selectedOption ||
        (selectedOption && dbDate(selectedDate) != selectedOption.dbDate)
      ) {
        const highestScoreOption = selectedDateOptions.reduce((prev, current) =>
          prev.scores.totalScore > current.scores.totalScore ? prev : current
        );
        if (highestScoreOption) {
          setSelectedOption(highestScoreOption);
        } else {
          setSelectedOption(null);
        }
      }
    } else {
      setSelectedOption(null);
    }
  }, [selectedDate]);

  const [
    submitVisit,
    { data: submitData, loading: submitLoading },
  ] = useScheduleVisitMutation();

  const safeSetSelectedOption = (option: DecoratedSchedulerOption | null) => {
    if (!submitLoading && !submitData) {
      setSelectedOption(option);
    }
  };

  const selectedDateOptions = useMemo(() => {
    const date = dbDate(selectedDate);
    return scheduleOptions.filter((option) => option.dbDate === date);
  }, [selectedDate, scheduleOptions]);

  const notFound = !loading && !visit && !selectedPatient;
  const anyLoading = loading || scheduleLoading;

  const handleSubmit = async (optionToSubmit?: DecoratedSchedulerOption, retries: number = 0) => {
    if (!optionToSubmit) {
      console.error("Selected option missing");
      return;
    }
    if (!visitType || !patient) { return; }
    setSubmitting(true);

    try {
      await submitVisit({
        variables: {
          patientId: patient.id,
          startTime: optionToSubmit.startTime,
          visitTypeId: visitType.id,
          duration: queryFilterValues.duration,
          serviceIds: queryFilterValues.serviceIds,
          resources: optionToSubmit.resources.map((resource) => ({
            providerId: resource.providerId,
            shiftId: resource.shiftId,
            raceConditionHash: resource.raceConditionHash,
          })),
          serviceInstructions,
          note: visitNote,
          runId: optionToSubmit.runId,
          reschedulingVisitId: visit?.id || null,
          serviceLineId,
          organizationalUnitId: episodeOrganizationalUnitId,
          chosenOptionIndex: optionToSubmit.index,
        },
      });
      setSubmitting(false);
      navigate(`/patients/${patient.id}`);
    } catch (err) {
      const error = err as ApolloError | undefined;

      const raceErr = error?.graphQLErrors?.find(
        (gqlErr) => gqlErr?.extensions?.code === SchedulingError.RaceCondition
      );

      if (!raceErr) {
        console.error("Error submitting visit:", error);
        setSubmitting(false); 
        return;
      }

      // Refetch new options to clear race condition
      const refetchResult = await refetchSchedule()
      const options = refetchResult.data?.scheduleOption?.options || [];
      const decoratedOptions = options.map((option, index) => ({
        ...option,
        dbDate: dbDate(option.startTime),
        index,
      }));

      const similarOption = findSimilarOption(optionToSubmit, decoratedOptions);

      if (similarOption && retries < MAX_RACE_CONDITION_RETRIES) {
        // Auto-submit similar option
        setSelectedOption(similarOption);
        await handleSubmit(similarOption, retries + 1);
      } else {
        // Notify user to pick a new option
        alert(raceErr.message);
        setSelectedOption(null);
      }
      setSubmitting(false);      
    }
  };

  const areFiltersSet = Object.entries(filterValues).some(([_key, value]) => Array.isArray(value) ? value.length > 0 : !!value);

  const continuityOfCareProviders = useMemo(() => {
    return scheduleProvidersData?.scheduleProvidersList.filter((p) => p.preferred).map((p) => {
      const providerTypesList = Object.values(providerTypes);
      const providerType = providerTypesList.find((pt) => pt.id === p.providerTypeId);
      return {
        id: p.id,
        firstName: p.firstName,
        lastName: `(${startCase(providerType?.name)})`,
        providerTypeId: providerType?.id ?? 0,
      }
    }) ?? [];
  }, [scheduleProvidersData?.scheduleProvidersList, providerTypes]);

  const hasContinuityOfCareProviders = useMemo(() => {
    return scheduleProvidersData?.scheduleProvidersList?.some((p) => p.preferred);
  }, [scheduleProvidersData?.scheduleProvidersList]);

  const isMissingContinuityProviders = useMemo(() => {
    if (!hasContinuityOfCareProviders || !continuityOfCareProviders?.length || !visitType?.visitResourceRequirements?.length) {
      return false;
    }
    
    // Check if any continuity providers are missing from the current filter selection
    const selectedProviderIds = new Set(filterValues.provider);
    const selectedCareProviders = continuityOfCareProviders.filter(provider => selectedProviderIds.has(provider.id.toString()));

    return selectedCareProviders.length < continuityOfCareProviders?.length;
  }, [hasContinuityOfCareProviders, continuityOfCareProviders, visitType?.visitResourceRequirements, filterValues.provider]);

  const visitTypeRequiresContinuityOfCare = useMemo(() => {
    return visitType?.visitResourceRequirements.some(vrr => vrr.requiresContinuityOfCare);
  }, [visitType]);


  const resetToContinuityProviders = useCallback(() => {
    if (continuityOfCareProviders?.length) {
      setFilterValues({
        ...filterValues,
        provider: continuityOfCareProviders.map(p => p.id.toString()),
      });
    }
  }, [continuityOfCareProviders, setFilterValues]);

  useEffect(resetToContinuityProviders, [scheduleProvidersData?.scheduleProvidersList]);

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4 }}>
      {notFound ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          Task not found.
        </Alert>
      ) : null}

      <Grid container spacing={3}>
        <Grid size={12}>
          <SchedulerPatientBrief
            patient={patient}
            loading={!visit && !selectedPatient}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 12, md: 9, lg: 9 }}>
          <Typography gutterBottom>
            <Typography fontWeight={500} component="span">
              Next visit due:
            </Typography>
            <Typography component="span" ml={0.75}>
              {loading ? (
                <Skeleton
                  variant="text"
                  width="140px"
                  sx={{ display: "inline-block" }}
                />
              ) : (
                visitType ? `${visitType.name}, ${datef(queryFilterValues.targetTime, "MMM do")}` : ""
              )}
            </Typography>
            <Button
              onClick={() => setParametersModalOpen(true)}
              variant="text"
              sx={{ ml: 0.75 }}
              size="small"
            >
              (change)
            </Button>
          </Typography>
          {creatingEpisode && organizationalUnitsError && (
            <Alert severity="error" sx={{ mb: 2 }}>Couldn't load organizational units.</Alert>
          )}
          {organizationalUnitSegmentationEnabled && creatingEpisode && organizationUserId != null && !organizationalUnitsLoading && !organizationalUnitsError && activeOrganizationalUnits.length === 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>No active organizational units are available.</Alert>
          )}
          <DashboardTile>
            <Box mb={2}>
              <Stack direction="row" flexWrap="wrap">
                <SchedulerFilterChips
                  onClick={() => setFilterDialogOpen(true)}
                  values={filterValues}
                  setValues={setFilterValues}
                  allowUseOverflowShifts={visitTypeAllowsOverflowShifts}
                  providers={scheduleProvidersData?.scheduleProvidersList || []}
                />

                <Box m={0.5}>
                  {areFiltersSet ? <IconButton
                      onClick={() => setFilterValues({ ...defaultFilters })}
                      size="small"
                      color="secondary"
                    >
                      <FilterAltOffIcon />
                  </IconButton> : <IconButton
                    onClick={() => setFilterDialogOpen(true)}
                    size="small"
                    color="secondary"
                  >
                    <FilterAltIcon />
                  </IconButton>}
                </Box>
              </Stack>
            </Box>
            {scheduleOptions.length === 0 && !anyLoading && (!isMarketplacePatient || parametersApplied) ? (
              <Box mb={2}>
                <Typography variant="h6" color="textSecondary">
                  No Options Available
                </Typography>
                {areFiltersSet ? (
                  <Typography variant="body2" color="textSecondary">
                    Try changing your filters
                  </Typography>
                ) : null}

                {isMarketplacePatient && (
                  <Box sx={{ mt: 3, p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                      No providers are available for the requested window.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      The scheduling optimizer could not find any providers with open shifts in the area.
                      Choose a recourse option below.
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Stack spacing={1.5}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<AssignmentLateIcon />}
                          sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                          onClick={() => {
                            const now = new Date();
                            const windowStart = new Date(now.getTime() + 2 * 60 * 60 * 1000);
                            const windowEnd = new Date(now.getTime() + 5 * 60 * 60 * 1000);
                            addMarketListVisit({
                              id: Date.now(),
                              patientFirstName: patient?.firstName ?? 'Visit',
                              patientLastName: patient?.lastName ?? 'Marketplace',
                              startTime: windowStart.toISOString(),
                              endTime: new Date(windowStart.getTime() + (parseInt(parameterValues.duration) || 60) * 60 * 1000).toISOString(),
                              arrivalWindowStart: windowStart.toISOString(),
                              arrivalWindowEnd: windowEnd.toISOString(),
                              visitTypeName: parameterValues.visitTypeId
                                ? (visitType?.name ?? 'Visit')
                                : 'Visit',
                            });
                            showSnackbar('Visit added to marketplace — providers can now claim it', 'success', {
                              label: 'View Marketplace',
                              onClick: () => navigate('/visit-alerts?tab=unassigned'),
                            });
                          }}
                        >
                          Add to Marketplace
                        </Button>
                        <Typography variant="body2" color="text.secondary" sx={{ pt: 0.5 }}>
                          Post as unassigned so any available provider can claim it from the Unassigned Visits list.
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<PhoneInTalkIcon />}
                          sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                          onClick={() => showSnackbar('On-call notification sent to 3 nearby providers', 'info')}
                        >
                          Notify On-Call
                        </Button>
                        <Typography variant="body2" color="text.secondary" sx={{ pt: 0.5 }}>
                          Alert on-call providers who may be able to take the visit outside their normal schedule.
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                )}
              </Box>
            ) : (
              <>
                <Box mb={2}>
                  {isMissingContinuityProviders && patient && !anyLoading && (
                    <CareTeamWarning
                      patient={patient}
                      careTeamProviders={continuityOfCareProviders}
                      applyCareTeam={resetToContinuityProviders}
                    />
                  )}
                  <SchedulerBestAvailableOptions
                    options={best3Options}
                    selectedOption={selectedOption}
                    setSelectedOption={safeSetSelectedOption}
                    timezone={timezone ?? 'America/Los_Angeles'}
                  />
                </Box>
                <Grid
                  container
                  direction="row"
                  justifyContent="space-between"
                >
                  {/* <Box> */}
                  <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 4 }}>
                    {anyLoading ? (
                      <>
                        <Skeleton
                          variant="text"
                          width="160px"
                          sx={{ mb: 1, ml: 2 }}
                        />
                        <DayCalendarSkeleton />
                      </>
                    ) : (
                      <SchedulerMonthCalendar
                        value={selectedDate}
                        onChange={setSelectedDate}
                        dateBadges={dateBadges}
                      />
                    )}
                  </Grid>
                  {/* </Box> */}

                  <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 8 }}>
                    {anyLoading ? null : (
                      <SchedulerAvailableTimes
                        selectedDate={selectedDate}
                        options={selectedDateOptions}
                        onChange={safeSetSelectedOption}
                        selectedOption={selectedOption}
                        providersById={providersById}
                        timezone={timezone ?? 'America/Los_Angeles'}
                      />
                    )}
                  </Grid>
                </Grid>
                <Box mt={4} p={2} bgcolor="grey.100" textAlign="left">
                  {anyLoading ? null : (
                    <Typography variant="body1">
                      {timezone &&
                        `All options are in ${timezoneAbbreviation(
                          timezone
                        )}`}
                      . Times displayed are arrival windows. Expected
                      visit duration is
                      {"  "}
                      {queryFilterValues?.duration} {"  "} minutes.
                    </Typography>
                  )}
                  {!anyLoading && visitTypeRequiresContinuityOfCare && hasContinuityOfCareProviders && !isMissingContinuityProviders && <Typography variant="body1" mt={2}>Care team filter applied</Typography>}
                </Box>
              </>
            )}
          </DashboardTile>
        </Grid>

        <Grid size={{ xs: 12, sm: 12, md: 3, lg: 3 }}>
          <SchedulerSelectedOptionDetail
            selectedOption={selectedOption}
            serviceNames={queryFilterValues.serviceNames}
            reschedulingVisit={visit}
            providersById={providersById}
            timezone={timezone ?? 'America/Los_Angeles'}
          />
          {selectedOption && <>
            {organizationalUnitSegmentationEnabled === false && creatingEpisode && activeOrganizationalUnits.length > 1 && (
              <Box mt={2}>
                <LabeledSelect
                  label="Organizational unit"
                  required
                  fullWidth
                  size="small"
                  value={parameterValues.organizationalUnitId?.toString() ?? ""}
                  onChange={(event) => setParameterValues({ ...parameterValues, organizationalUnitId: Number(event.target.value) || undefined })}
                >
                  {activeOrganizationalUnits.map((unit) => (
                    <MenuItem key={unit.id} value={unit.id.toString()}>{unit.name}</MenuItem>
                  ))}
                </LabeledSelect>
              </Box>
            )}
            <Box mt={2}>
              <Typography variant="subtitle1" mt={1.5} fontWeight={500}>
                Patient Service Instructions
              </Typography>
              <ServiceInstructionsInput value={serviceInstructions} onChange={setServiceInstructions} />
            </Box>
            <Box mt={2}>
              <Typography variant="subtitle1" mt={1.5} fontWeight={500}>
                Visit Note
              </Typography>
              <VisitNoteInput value={visitNote} onChange={setVisitNote} />
            </Box>
            <Box mt={2}>
              <Button
                variant="contained"
                onClick={() => handleSubmit(selectedOption)}
                fullWidth
                size="large"
                disabled={!selectedOption || submitting || requiresOrganizationalUnitSelection || (organizationalUnitSegmentationEnabled && (scheduleLoading || !shouldFetchScheduleData))}
                loading={submitting}
              >
                Schedule
              </Button>
            </Box>
          </>}
        </Grid>
      </Grid>

      {filterDialogOpen && patient && visitType && <SchedulerFilterDialog
        providers={scheduleProvidersData?.scheduleProvidersList || []}
        visitType={visitType}
        patient={patient}
        onClose={() => setFilterDialogOpen(false)}
        values={filterValues}
        setValues={setFilterValues}
      />}
      {parametersModalOpen && <SchedulerParametersDialog
        values={parameterValues}
        onClose={() => setParametersModalOpen(false)}
        onSubmit={(v) => { console.log(v); setParameterValues(v); setParametersApplied(true); }}
        rescheduling={!!visit}
        dialogWarning={null /* TODO */}
        episodes={patient?.episodes ?? []}
        organizationalUnits={organizationalUnitSegmentationEnabled === false || noActiveOrganizationalUnits ? undefined : activeOrganizationalUnits}
        requireExplicitOrganizationalUnitSelection={organizationUnits.length > 1}
      />}
    </Container>
  );
}

const DEFAULT_TASK_DETAIL: TaskDetail = {
  serviceLineId: "",
  visitTypeId: "",
  serviceIds: [],
  serviceNames: [],
  duration: "0",
  episodeId: "",
  constraints: {},
};

const findSimilarOption = (
  original: DecoratedSchedulerOption,
  options: DecoratedSchedulerOption[]
): DecoratedSchedulerOption | null => {
  const originalProviderIds = original.resources.map((r) => r.providerId).sort();

  return (
    options.find((opt) => {
      // 1. Arrival window matches exactly
      if (
        opt.arrivalWindowStart !== original.arrivalWindowStart ||
        opt.arrivalWindowEnd !== original.arrivalWindowEnd
      ) {
        return false;
      }

      // 2. Provider IDs match exactly
      const optProviderIds = opt.resources.map((r) => r.providerId).sort();
      if (
        optProviderIds.length !== originalProviderIds.length ||
        !optProviderIds.every((id, idx) => id === originalProviderIds[idx])
      ) {
        return false;
      }

      // 3. driveForScoring difference <= 5
      if (opt.driveForScoring - original.driveForScoring > 5) {
        return false;
      }

      // All conditions satisfied
      return true;
    }) || null
  );
};

gql`
  query SchedulerOptions(
    $patientId: Int!
    $startTime: String!
    $targetTime: String!
    $endTime: String!
    $serviceLineId: Int!
    $organizationalUnitId: Int
    $visitTypeId: Int!
    $duration: Int!
    $serviceIds: [Int!]!
    $providerTraits: [ProvideTraitQueryInput]!
    $daysOfWeek: [Int]
    $hoursOfDay: HoursOfDayQueryInput
    $reschedulingVisitId: Int
    $selectedProviderIds: [Int!]
    $useOverflowShifts: Boolean
  )
  @permission(requires: scheduleVisits, check: [userInServiceLine])
  {
    scheduleOption(
      patientId: $patientId
      startTime: $startTime
      targetTime: $targetTime
      endTime: $endTime
      serviceLineId: $serviceLineId
      organizationalUnitId: $organizationalUnitId
      visitTypeId: $visitTypeId
      duration: $duration
      serviceIds: $serviceIds
      providerTraits: $providerTraits
      daysOfWeek: $daysOfWeek
      hoursOfDay: $hoursOfDay
      reschedulingVisitId: $reschedulingVisitId
      selectedProviderIds: $selectedProviderIds
      useOverflowShifts: $useOverflowShifts
    ) {
      options {
        startTime
        endTime
        arrivalWindowEnd
        arrivalWindowStart
        rankCategory
        originDrive
        destinationDrive
        driveForScoring
        contiguous
        resources {
          startTime
          endTime
          inHome
          providerId
          shiftId
          raceConditionHash
          preferred
        }
        scores {
          totalScore
          driveScore
          proximityScore
          utilizationScore
        }
        runId
      }
      providers {
        id
        firstName
        lastName
      }
    }
  }
`;

gql`
  query SchedulerProvidersList(
    $patientId: Int!
    $startTime: String!
    $endTime: String!
    $serviceLineId: Int!
    $organizationalUnitId: Int
    $visitTypeId: Int!
  )
  @permission(requires: scheduleVisits, check: [userInServiceLine])
  {
    scheduleProvidersList(
      patientId: $patientId
      startTime: $startTime
      endTime: $endTime
      serviceLineId: $serviceLineId
      organizationalUnitId: $organizationalUnitId
      visitTypeId: $visitTypeId
    ) {
      id
      firstName
      lastName
      preferred
      providerTypeId
      hasProviderType
      hasServiceLine
      hasStateLicense
      hasNearbyShifts
      hasVirtualShifts
      hasInHomeShifts
      hasNonOverflowShifts
    }
  }
`;

gql`
  mutation ScheduleVisit(
    $patientId: Int!
    $startTime: String!
    $visitTypeId: Int!
    $duration: Int!
    $resources: [NewVisitResourceInput!]!
    $serviceIds: [Int!]!
    $serviceInstructions: String
    $note: String
    $runId: String!
    $reschedulingVisitId: Int
    $serviceLineId: Int!
    $organizationalUnitId: Int
    $chosenOptionIndex: Int
  )
  @permission(requires: scheduleVisits, check: [userInServiceLine])
  {
    scheduleVisit(
      patientId: $patientId
      startTime: $startTime
      visitTypeId: $visitTypeId
      duration: $duration
      resources: $resources
      serviceIds: $serviceIds
      serviceInstructions: $serviceInstructions
      note: $note
      runId: $runId
      reschedulingVisitId: $reschedulingVisitId
      serviceLineId: $serviceLineId
      organizationalUnitId: $organizationalUnitId
      chosenOptionIndex: $chosenOptionIndex
    ) {
      id
      startTime
      endTime
      arrivalWindowStartTime
      arrivalWindowEndTime
      serviceInstructions
      patient {
        id
        serviceInstructions
      }
    }
  }
`;

gql`
  query SchedulerOrganizationalUnits($organizationUserId: Int!) @permission(requires: scheduleVisits) {
    organizationalUnit(orderBy: { name: { direction: asc, priority: 1 } }) {
      id
      name
      active
    }
    organizationalUnitOrgUser(where: { organizationUserId: { eq: $organizationUserId } }) {
      organizationalUnit {
        id
        name
        active
      }
    }
  }
`;
